import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { canRead } from '@/lib/permissions';
import type { Inserts, Json, Tables, Updates } from '@/types/database';
import { DEFAULT_SETTINGS, type WorkbookSettings } from './engine';

/**
 * Data layer for the Projections workbook (migration 146). Every hook here
 * targets a super_admin-only table; the `enabled` gate mirrors the one in
 * `hooks.ts` so non-super users never fire a request RLS would reject.
 *
 * Editing hooks apply OPTIMISTIC updates to the React Query cache: the grid
 * derives its rows from query state, so without them every keystroke would
 * flicker back to the server value for a round-trip. On error the cache is
 * rolled back and the query refetched.
 */
function useWorkbookEnabled(): boolean {
  const { role, loading } = useAuth();
  return !loading && canRead(role, 'financials');
}

export const BASE_SCENARIO = 'base';

// ---------------------------------------------------------------------------
// Workbooks / scenarios
// ---------------------------------------------------------------------------

export type Workbook = Tables<'projections_workbooks'>;

export function useWorkbooks() {
  const enabled = useWorkbookEnabled();
  return useQuery({
    queryKey: ['projections-workbooks'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projections_workbooks')
        .select('*')
        .order('created_at');
      if (error) throw error;
      return data as Workbook[];
    },
  });
}

export function parseSettings(raw: Json | null | undefined): WorkbookSettings {
  const obj = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<
    string,
    unknown
  >;
  const num = (k: keyof WorkbookSettings, fallback: number | null): number | null => {
    const v = obj[k];
    return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
  };
  return {
    min_cash_cents: num('min_cash_cents', DEFAULT_SETTINGS.min_cash_cents) ?? 0,
    variance_pct_threshold:
      num('variance_pct_threshold', DEFAULT_SETTINGS.variance_pct_threshold) ?? 0.1,
    variance_abs_cents: num('variance_abs_cents', DEFAULT_SETTINGS.variance_abs_cents) ?? 0,
    gross_margin_floor_pct:
      num('gross_margin_floor_pct', DEFAULT_SETTINGS.gross_margin_floor_pct) ?? 0,
    fiscal_year_start_month:
      num('fiscal_year_start_month', DEFAULT_SETTINGS.fiscal_year_start_month) ?? 1,
    opening_cash_cents: num('opening_cash_cents', null),
  };
}

export function useEnsureBaseWorkbook() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async () => {
      if (!profile) throw new Error('Not signed in');
      const row: Inserts<'projections_workbooks'> = {
        organization_id: profile.organization_id,
        scenario: BASE_SCENARIO,
        name: 'Base plan',
        settings: {},
      };
      const { error } = await supabase
        .from('projections_workbooks')
        .upsert(row, { onConflict: 'organization_id,scenario', ignoreDuplicates: true });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projections-workbooks'] }),
  });
}

export function useUpdateWorkbook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Updates<'projections_workbooks'>) => {
      const { data, error } = await supabase
        .from('projections_workbooks')
        .update(patch)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as Workbook;
    },
    onMutate: async ({ id, ...patch }) => {
      await qc.cancelQueries({ queryKey: ['projections-workbooks'] });
      const prev = qc.getQueryData<Workbook[]>(['projections-workbooks']);
      qc.setQueryData<Workbook[]>(['projections-workbooks'], (old) =>
        (old ?? []).map((w) => (w.id === id ? { ...w, ...patch } : w)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['projections-workbooks'], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['projections-workbooks'] }),
  });
}

export function useCloneScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { from: string; to: string; name?: string }) => {
      const { error } = await supabase.rpc('projections_clone_scenario', {
        p_from: input.from,
        p_to: input.to,
        p_name: input.name ?? null,
      });
      if (error) throw error;
      return input.to;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projections-workbooks'] }),
  });
}

export function useDeleteScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (scenario: string) => {
      const { error } = await supabase.rpc('projections_delete_scenario', { p_scenario: scenario });
      if (error) throw error;
      return scenario;
    },
    onSuccess: () =>
      qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('projections-') }),
  });
}

// ---------------------------------------------------------------------------
// Sales model
// ---------------------------------------------------------------------------

export type SalesProductRow = Tables<'projections_sales_products'>;
export type SalesCustomerRow = Tables<'projections_sales_customers'>;
export type SalesUnitRow = Tables<'projections_sales_units'>;

export function useSalesProducts(scenario: string) {
  const enabled = useWorkbookEnabled();
  return useQuery({
    queryKey: ['projections-sales-products', scenario],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projections_sales_products')
        .select('*')
        .eq('scenario', scenario)
        .order('position');
      if (error) throw error;
      return data as SalesProductRow[];
    },
  });
}

export function useUpsertSalesProduct(scenario: string) {
  const qc = useQueryClient();
  const { profile } = useAuth();
  const key = ['projections-sales-products', scenario];
  return useMutation({
    mutationFn: async (input: Partial<SalesProductRow> & { name: string }) => {
      if (!profile) throw new Error('Not signed in');
      const row: Inserts<'projections_sales_products'> = {
        ...input,
        organization_id: profile.organization_id,
        scenario,
      };
      const { data, error } = await supabase
        .from('projections_sales_products')
        .upsert(row)
        .select('*')
        .single();
      if (error) throw error;
      return data as SalesProductRow;
    },
    onMutate: async (input) => {
      if (!input.id) return;
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<SalesProductRow[]>(key);
      qc.setQueryData<SalesProductRow[]>(key, (old) =>
        (old ?? []).map((p) => (p.id === input.id ? { ...p, ...input } : p)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useDeleteSalesProduct(scenario: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('projections_sales_products').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projections-sales-products', scenario] }),
  });
}

export function useSalesCustomers(scenario: string) {
  const enabled = useWorkbookEnabled();
  return useQuery({
    queryKey: ['projections-sales-customers', scenario],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projections_sales_customers')
        .select('*')
        .eq('scenario', scenario)
        .order('position');
      if (error) throw error;
      return data as SalesCustomerRow[];
    },
  });
}

export function useUpsertSalesCustomer(scenario: string) {
  const qc = useQueryClient();
  const { profile } = useAuth();
  const key = ['projections-sales-customers', scenario];
  return useMutation({
    mutationFn: async (input: Partial<SalesCustomerRow> & { name: string }) => {
      if (!profile) throw new Error('Not signed in');
      const row: Inserts<'projections_sales_customers'> = {
        ...input,
        organization_id: profile.organization_id,
        scenario,
      };
      const { data, error } = await supabase
        .from('projections_sales_customers')
        .upsert(row)
        .select('*')
        .single();
      if (error) throw error;
      return data as SalesCustomerRow;
    },
    onMutate: async (input) => {
      if (!input.id) return;
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<SalesCustomerRow[]>(key);
      qc.setQueryData<SalesCustomerRow[]>(key, (old) =>
        (old ?? []).map((c) => (c.id === input.id ? { ...c, ...input } : c)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useDeleteSalesCustomer(scenario: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('projections_sales_customers').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projections-sales-customers', scenario] });
      qc.invalidateQueries({ queryKey: ['projections-sales-units', scenario] });
    },
  });
}

export function useSalesUnits(scenario: string) {
  const enabled = useWorkbookEnabled();
  return useQuery({
    queryKey: ['projections-sales-units', scenario],
    enabled,
    queryFn: async () => {
      // Units carry no scenario column — scope through the parent customer.
      const { data, error } = await supabase
        .from('projections_sales_units')
        .select('*, projections_sales_customers!inner(scenario)')
        .eq('projections_sales_customers.scenario', scenario);
      if (error) throw error;
      return (
        data as unknown as Array<SalesUnitRow & { projections_sales_customers: unknown }>
      ).map(({ projections_sales_customers: _c, ...rest }) => rest as SalesUnitRow);
    },
  });
}

export function useUpsertSalesUnits(scenario: string) {
  const qc = useQueryClient();
  const { profile } = useAuth();
  const key = ['projections-sales-units', scenario];
  return useMutation({
    mutationFn: async (cells: Array<{ customer_id: string; month: string; units: number }>) => {
      if (!profile) throw new Error('Not signed in');
      if (cells.length === 0) return 0;
      const rows: Inserts<'projections_sales_units'>[] = cells.map((c) => ({
        organization_id: profile.organization_id,
        customer_id: c.customer_id,
        month: c.month,
        units: c.units,
      }));
      const { error } = await supabase
        .from('projections_sales_units')
        .upsert(rows, { onConflict: 'customer_id,month' });
      if (error) throw error;
      return rows.length;
    },
    onMutate: async (cells) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<SalesUnitRow[]>(key);
      qc.setQueryData<SalesUnitRow[]>(key, (old) => {
        const next = [...(old ?? [])];
        for (const c of cells) {
          const i = next.findIndex((r) => r.customer_id === c.customer_id && r.month === c.month);
          if (i >= 0) next[i] = { ...next[i], units: c.units };
          else
            next.push({
              id: `tmp-${c.customer_id}-${c.month}`,
              organization_id: profile?.organization_id ?? '',
              customer_id: c.customer_id,
              month: c.month,
              units: c.units,
              notes: null,
              created_at: '',
              updated_at: '',
            });
        }
        return next;
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

// ---------------------------------------------------------------------------
// Balance-sheet movements (planned change in balance per account / month)
// ---------------------------------------------------------------------------

export type BalanceMovementRow = Tables<'projections_balance_movements'>;

export function useBalanceMovements(scenario: string) {
  const enabled = useWorkbookEnabled();
  return useQuery({
    queryKey: ['projections-balance-movements', scenario],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projections_balance_movements')
        .select('*')
        .eq('scenario', scenario);
      if (error) throw error;
      return data as BalanceMovementRow[];
    },
  });
}

export function useUpsertBalanceMovements(scenario: string) {
  const qc = useQueryClient();
  const { profile } = useAuth();
  const key = ['projections-balance-movements', scenario];
  return useMutation({
    mutationFn: async (
      cells: Array<{ account_id: string; month: string; delta_cents: number }>,
    ) => {
      if (!profile) throw new Error('Not signed in');
      if (cells.length === 0) return 0;
      const rows: Inserts<'projections_balance_movements'>[] = cells.map((c) => ({
        organization_id: profile.organization_id,
        account_id: c.account_id,
        month: c.month,
        delta_cents: c.delta_cents,
        scenario,
      }));
      const { error } = await supabase
        .from('projections_balance_movements')
        .upsert(rows, { onConflict: 'organization_id,account_id,month,scenario' });
      if (error) throw error;
      return rows.length;
    },
    onMutate: async (cells) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<BalanceMovementRow[]>(key);
      qc.setQueryData<BalanceMovementRow[]>(key, (old) => {
        const next = [...(old ?? [])];
        for (const c of cells) {
          const i = next.findIndex((r) => r.account_id === c.account_id && r.month === c.month);
          if (i >= 0) next[i] = { ...next[i], delta_cents: c.delta_cents };
          else
            next.push({
              id: `tmp-${c.account_id}-${c.month}`,
              organization_id: profile?.organization_id ?? '',
              account_id: c.account_id,
              month: c.month,
              delta_cents: c.delta_cents,
              scenario,
              notes: null,
              created_at: '',
              updated_at: '',
            });
        }
        return next;
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

// ---------------------------------------------------------------------------
// Planned P&L cells — same table as before (projections_pnl) but with an
// optimistic bulk upsert so typing into the grid is instant.
// ---------------------------------------------------------------------------

export type PnlPlanRow = Tables<'projections_pnl'>;

export function usePnlPlans(scenario: string) {
  const enabled = useWorkbookEnabled();
  return useQuery({
    queryKey: ['projections-pnl', scenario],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projections_pnl')
        .select('*')
        .eq('scenario', scenario);
      if (error) throw error;
      return data as PnlPlanRow[];
    },
  });
}

export function useUpsertPnlPlans(scenario: string) {
  const qc = useQueryClient();
  const { profile } = useAuth();
  const key = ['projections-pnl', scenario];
  return useMutation({
    mutationFn: async (
      cells: Array<{ account_id: string; month: string; amount_cents: number }>,
    ) => {
      if (!profile) throw new Error('Not signed in');
      if (cells.length === 0) return 0;
      const rows: Inserts<'projections_pnl'>[] = cells.map((c) => ({
        organization_id: profile.organization_id,
        account_id: c.account_id,
        month: c.month,
        amount_cents: c.amount_cents,
        scenario,
      }));
      const { error } = await supabase
        .from('projections_pnl')
        .upsert(rows, { onConflict: 'organization_id,account_id,month,scenario' });
      if (error) throw error;
      return rows.length;
    },
    onMutate: async (cells) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<PnlPlanRow[]>(key);
      qc.setQueryData<PnlPlanRow[]>(key, (old) => {
        const next = [...(old ?? [])];
        for (const c of cells) {
          const i = next.findIndex((r) => r.account_id === c.account_id && r.month === c.month);
          if (i >= 0) next[i] = { ...next[i], amount_cents: c.amount_cents };
          else
            next.push({
              id: `tmp-${c.account_id}-${c.month}`,
              organization_id: profile?.organization_id ?? '',
              account_id: c.account_id,
              month: c.month,
              amount_cents: c.amount_cents,
              scenario,
              notes: null,
              created_at: '',
              updated_at: '',
            });
        }
        return next;
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

// ---------------------------------------------------------------------------
// Sheet rows (custom lines + account overrides) and their cells
// ---------------------------------------------------------------------------

export type SheetRow = Tables<'projections_sheet_rows'>;
export type SheetCellRow = Tables<'projections_sheet_cells'>;

export function useSheetRows(scenario: string) {
  const enabled = useWorkbookEnabled();
  return useQuery({
    queryKey: ['projections-sheet-rows', scenario],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projections_sheet_rows')
        .select('*')
        .eq('scenario', scenario)
        .order('created_at');
      if (error) throw error;
      return data as SheetRow[];
    },
  });
}

export function useUpsertSheetRow(scenario: string) {
  const qc = useQueryClient();
  const { profile } = useAuth();
  const key = ['projections-sheet-rows', scenario];
  return useMutation({
    mutationFn: async (input: Partial<SheetRow> & { kind: SheetRow['kind'] }) => {
      if (!profile) throw new Error('Not signed in');
      const row: Inserts<'projections_sheet_rows'> = {
        ...input,
        organization_id: profile.organization_id,
        scenario,
        sheet: input.sheet ?? 'main',
      };
      const { data, error } = await supabase
        .from('projections_sheet_rows')
        .upsert(row)
        .select('*')
        .single();
      if (error) throw error;
      return data as SheetRow;
    },
    onMutate: async (input) => {
      if (!input.id) return;
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<SheetRow[]>(key);
      qc.setQueryData<SheetRow[]>(key, (old) =>
        (old ?? []).map((r) => (r.id === input.id ? { ...r, ...input } : r)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

/**
 * Account-row overrides are keyed by (scenario, sheet, account_id); this
 * helper upserts against that unique index so hide / relabel is idempotent.
 */
export function useSetAccountOverride(scenario: string) {
  const qc = useQueryClient();
  const { profile } = useAuth();
  const key = ['projections-sheet-rows', scenario];
  return useMutation({
    mutationFn: async (input: {
      account_id: string;
      sheet?: string;
      hidden?: boolean;
      label?: string | null;
      section?: string | null;
    }) => {
      if (!profile) throw new Error('Not signed in');
      const sheet = input.sheet ?? 'main';
      const existing = await supabase
        .from('projections_sheet_rows')
        .select('id')
        .eq('scenario', scenario)
        .eq('sheet', sheet)
        .eq('account_id', input.account_id)
        .maybeSingle();
      if (existing.error) throw existing.error;
      const patch: Updates<'projections_sheet_rows'> = {};
      if (input.hidden !== undefined) patch.hidden = input.hidden;
      if (input.label !== undefined) patch.label = input.label;
      if (input.section !== undefined) patch.section = input.section;
      if (existing.data) {
        const { error } = await supabase
          .from('projections_sheet_rows')
          .update(patch)
          .eq('id', existing.data.id);
        if (error) throw error;
      } else {
        const row: Inserts<'projections_sheet_rows'> = {
          organization_id: profile.organization_id,
          scenario,
          sheet,
          kind: 'account',
          account_id: input.account_id,
          ...patch,
        };
        const { error } = await supabase.from('projections_sheet_rows').insert(row);
        if (error) throw error;
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useDeleteSheetRow(scenario: string) {
  const qc = useQueryClient();
  const key = ['projections-sheet-rows', scenario];
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('projections_sheet_rows').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<SheetRow[]>(key);
      qc.setQueryData<SheetRow[]>(key, (old) => (old ?? []).filter((r) => r.id !== id));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ['projections-sheet-cells', scenario] });
    },
  });
}

export function useSheetCells(scenario: string) {
  const enabled = useWorkbookEnabled();
  return useQuery({
    queryKey: ['projections-sheet-cells', scenario],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projections_sheet_cells')
        .select('*, projections_sheet_rows!inner(scenario)')
        .eq('projections_sheet_rows.scenario', scenario);
      if (error) throw error;
      return (data as unknown as Array<SheetCellRow & { projections_sheet_rows: unknown }>).map(
        ({ projections_sheet_rows: _r, ...rest }) => rest as SheetCellRow,
      );
    },
  });
}

export function useUpsertSheetCells(scenario: string) {
  const qc = useQueryClient();
  const { profile } = useAuth();
  const key = ['projections-sheet-cells', scenario];
  return useMutation({
    mutationFn: async (cells: Array<{ row_id: string; month: string; value: number }>) => {
      if (!profile) throw new Error('Not signed in');
      if (cells.length === 0) return 0;
      const rows: Inserts<'projections_sheet_cells'>[] = cells.map((c) => ({
        organization_id: profile.organization_id,
        row_id: c.row_id,
        month: c.month,
        value: c.value,
      }));
      const { error } = await supabase
        .from('projections_sheet_cells')
        .upsert(rows, { onConflict: 'row_id,month' });
      if (error) throw error;
      return rows.length;
    },
    onMutate: async (cells) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<SheetCellRow[]>(key);
      qc.setQueryData<SheetCellRow[]>(key, (old) => {
        const next = [...(old ?? [])];
        for (const c of cells) {
          const i = next.findIndex((r) => r.row_id === c.row_id && r.month === c.month);
          if (i >= 0) next[i] = { ...next[i], value: c.value };
          else
            next.push({
              id: `tmp-${c.row_id}-${c.month}`,
              organization_id: profile?.organization_id ?? '',
              row_id: c.row_id,
              month: c.month,
              value: c.value,
              created_at: '',
              updated_at: '',
            });
        }
        return next;
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

// ---------------------------------------------------------------------------
// Cell styles (highlight colour + comment)
// ---------------------------------------------------------------------------

export type CellStyleRow = Tables<'projections_cell_styles'>;

export function useCellStyles(scenario: string) {
  const enabled = useWorkbookEnabled();
  return useQuery({
    queryKey: ['projections-cell-styles', scenario],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projections_cell_styles')
        .select('*')
        .eq('scenario', scenario);
      if (error) throw error;
      return data as CellStyleRow[];
    },
  });
}

export interface CellStyleInput {
  sheet: string;
  row_key: string;
  month: string | null;
  col: string | null;
  color?: string | null;
  note?: string | null;
}

export function useSetCellStyle(scenario: string) {
  const qc = useQueryClient();
  const { profile } = useAuth();
  const key = ['projections-cell-styles', scenario];
  const same = (a: CellStyleInput | CellStyleRow, b: CellStyleInput) =>
    a.sheet === b.sheet &&
    a.row_key === b.row_key &&
    (a.month ?? null) === (b.month ?? null) &&
    (a.col ?? null) === (b.col ?? null);
  return useMutation({
    mutationFn: async (input: CellStyleInput) => {
      if (!profile) throw new Error('Not signed in');
      const existing = (qc.getQueryData<CellStyleRow[]>(key) ?? []).find((r) => same(r, input));
      const color = input.color === undefined ? (existing?.color ?? null) : input.color;
      const note = input.note === undefined ? (existing?.note ?? null) : input.note;
      // Clearing both halves deletes the record outright.
      if (!color && !note) {
        let q = supabase
          .from('projections_cell_styles')
          .delete()
          .eq('scenario', scenario)
          .eq('sheet', input.sheet)
          .eq('row_key', input.row_key);
        q = input.month ? q.eq('month', input.month) : q.is('month', null);
        q = input.col ? q.eq('col', input.col) : q.is('col', null);
        const { error } = await q;
        if (error) throw error;
        return;
      }
      if (existing && !existing.id.startsWith('tmp-')) {
        const { error } = await supabase
          .from('projections_cell_styles')
          .update({ color, note })
          .eq('id', existing.id);
        if (error) throw error;
        return;
      }
      const row: Inserts<'projections_cell_styles'> = {
        organization_id: profile.organization_id,
        scenario,
        sheet: input.sheet,
        row_key: input.row_key,
        month: input.month,
        col: input.col,
        color,
        note,
      };
      const { error } = await supabase.from('projections_cell_styles').insert(row);
      if (error) throw error;
    },
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<CellStyleRow[]>(key);
      qc.setQueryData<CellStyleRow[]>(key, (old) => {
        const list = [...(old ?? [])];
        const i = list.findIndex((r) => same(r, input));
        const base: CellStyleRow =
          i >= 0
            ? list[i]
            : {
                id: `tmp-${input.row_key}-${input.month ?? ''}-${input.col ?? ''}`,
                organization_id: profile?.organization_id ?? '',
                scenario,
                sheet: input.sheet,
                row_key: input.row_key,
                month: input.month,
                col: input.col,
                color: null,
                note: null,
                created_at: '',
                updated_at: '',
              };
        const merged: CellStyleRow = {
          ...base,
          color: input.color === undefined ? base.color : input.color,
          note: input.note === undefined ? base.note : input.note,
        };
        if (!merged.color && !merged.note) {
          if (i >= 0) list.splice(i, 1);
          return list;
        }
        if (i >= 0) list[i] = merged;
        else list.push(merged);
        return list;
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

// ---------------------------------------------------------------------------
// AI insights
// ---------------------------------------------------------------------------

export type InsightRow = Tables<'projections_insights'>;

export interface Insight {
  severity: 'critical' | 'warning' | 'opportunity' | 'info';
  title: string;
  detail: string;
  suggestion?: string | null;
  row_key?: string | null;
  month?: string | null;
}

export function useLatestInsights(scenario: string) {
  const enabled = useWorkbookEnabled();
  return useQuery({
    queryKey: ['projections-insights', scenario],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projections_insights')
        .select('*')
        .eq('scenario', scenario)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as InsightRow | null) ?? null;
    },
  });
}

export interface GenerateInsightsResult {
  ok: boolean;
  model: string;
  summary: string;
  insights: Insight[];
  error?: string;
}

export function useGenerateInsights(scenario: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { snapshot: unknown; model?: string }) => {
      const { data, error } = await supabase.functions.invoke('projections-insights-ai', {
        body: { scenario, snapshot: input.snapshot, model: input.model },
      });
      if (error) throw error;
      const res = data as GenerateInsightsResult;
      if (!res?.ok) throw new Error(res?.error || 'AI analysis failed');
      return res;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projections-insights', scenario] }),
  });
}
